import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import * as dateFns from 'date-fns';
const { addMonths, format, isPast, isThisMonth, isToday, subMonths } = dateFns;

import {
  getCalendarDate,
  radioBehaviorByCheckbox,
} from '../../../../../helpers/merchants/resources/helper';
import {
  SchoolTimeSlot,
  TimeSlot,
  WaitingListErrors,
} from '../../../../../interfaces/merchants/resources/books';

import BooksDateCalendarHeader from '../common/calendar/BooksDateCalendarHeader';
import BooksDateCalendarMonth, {
  BooksDateCalendarDay,
} from '../common/calendar/BooksDateCalendarMonth';
import BooksSelectTimeEvent from '../common/inputs/BooksSelectTimeEvent';
import BooksSelectTimeInstructor from '../common/inputs/BooksSelectTimeInstructor';
import BooksLoadingMask from '../common/loadings/BooksLoadingMask';
import LabeledContainer from '../../../../common/LabeledContainer';
import InputBookingWaitingList from '../InputBookingWaitingList';
import { RSButton } from 'src/reservation-page/RSButton';

import css from './InputSchoolCalendarDateTimePicker.module.scss';

interface Props {
  postable: boolean;
  selection: 'single' | 'multiple';
  baseDate: Date;
  timeSlots: { [date: string]: TimeSlot[] };
  showVacancy: boolean;
  onChange: (timeSlots: TimeSlot[]) => void;
  onChangeBaseDate: (baseDate: Date) => void;
  selectedTimeSlots: TimeSlot[];
  attendees?: number;
  waitingList?: {
    list: number[];
    onAddToWaitingList: (timeSlot: TimeSlot) => Promise<void>;
    loggedIn: boolean;
    errors?: WaitingListErrors;
  };
}

interface DateType {
  date: number;
  iso: string;
  type: string;
}

interface Calendar {
  baseDate: Date;
  dates: DateType[];
}

const isSchoolTimeSlot = (t: TimeSlot): t is SchoolTimeSlot =>
  t.hasOwnProperty('instructors');

const InputSchoolCalendarDateTimePicker: React.FC<Props> = (props) => {
  const {
    attendees,
    baseDate,
    onChange,
    onChangeBaseDate,
    postable,
    selection,
    showVacancy,
    timeSlots,
    waitingList,
  } = props;
  const intl = useIntl();

  const [calendar, setCalendar] = useState<Calendar>({ baseDate, dates: [] });
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    props.selectedTimeSlots.length === 0
      ? undefined
      : props.selectedTimeSlots[0].date,
  );
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeSlot[]>(
    props.selectedTimeSlots.concat(),
  );
  const [selectedWaitingTimeSlot, setSelectedWaitingTimeSlot] = useState<
    TimeSlot | undefined
  >(undefined);

  useEffect(() => {
    fillCalendarPropsBy(calendar.baseDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickNext = () => {
    const { baseDate } = calendar;
    fillCalendarPropsBy(addMonths(baseDate, 1));
  };

  const handleClickPrev = () => {
    const { baseDate } = calendar;
    fillCalendarPropsBy(subMonths(baseDate, 1));
  };

  const handleAddToWaitingList = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!waitingList) {
      return;
    }
    const { value } = e.currentTarget;
    Object.values(timeSlots).forEach((_timeSlots) => {
      const timeSlot = _timeSlots.find((t) => t.id.toString() === value);
      if (timeSlot) {
        if (!waitingList.loggedIn) {
          setSelectedWaitingTimeSlot(timeSlot);
          return;
        }
        waitingList.onAddToWaitingList(timeSlot);
        return;
      }
    });
  };

  const handleReturnCalendar = () => {
    setSelectedWaitingTimeSlot(undefined);
  };

  const fillCalendarPropsBy = async (baseDate: Date) => {
    const dates = getCalendarDate(baseDate);
    setCalendar({ baseDate, dates });
    onChangeBaseDate(baseDate);
  };

  const handleClickDate = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = e.currentTarget;
    setSelectedDate(value);
  };

  const newSelectedTimeSlots = (
    checked: boolean,
    newTimeSlot: TimeSlot,
  ): TimeSlot[] => {
    return radioBehaviorByCheckbox(
      selection,
      checked,
      [...selectedTimeSlots],
      newTimeSlot,
    );
  };

  const handleClickTimeSlot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget;
    const timeSlot = timeSlots[selectedDate!].find(
      (t) => t.id.toString() === value,
    );

    if (!timeSlot) {
      throw new Error(
        `timeSlot could not be found: value: ${value}, date: ${selectedDate}`,
      );
    }

    setSelectedTimeSlots(newSelectedTimeSlots(checked, timeSlot));
  };

  const handleClickCommit = () => {
    onChange(selectedTimeSlots);
  };

  const timeSlotCandidates = selectedDate ? timeSlots[selectedDate] : [];

  return selectedWaitingTimeSlot ? (
    <InputBookingWaitingList
      timeSlot={selectedWaitingTimeSlot}
      errors={waitingList!.errors}
      // @ts-expect-error strictFunctionTypes
      onAddToWaitingList={waitingList!.onAddToWaitingList}
      onReturnSelectDateTime={handleReturnCalendar}
      postable={postable}
    />
  ) : (
    <BooksLoadingMask isLoading={!postable}>
      <div className={css.InputSchoolCalendarDateTimePicker}>
        <BooksDateCalendarHeader
          baseDate={intl.formatDate(format(calendar.baseDate), {
            year: 'numeric',
            month: 'long',
            day: undefined,
          })}
          onClickPrev={handleClickPrev}
          onClickNext={handleClickNext}
          prevButtonDisabled={isThisMonth(calendar.baseDate)}
        />
        <BooksDateCalendarMonth>
          {calendar.dates.map((date, index) => (
            <BooksDateCalendarDay
              key={index}
              value={date.iso}
              date={date.date}
              onClick={handleClickDate}
              disabled={
                date.type !== 'current' ||
                (isPast(date.iso) && !isToday(date.iso)) ||
                !timeSlots[date.iso] ||
                (!!waitingList
                  ? false
                  : timeSlots[date.iso].every((s) => s.vacancy < 1))
              }
              current={date.type === 'current'}
              selected={selectedDate === date.iso}
            />
          ))}
        </BooksDateCalendarMonth>
        {selectedDate && timeSlotCandidates ? (
          <LabeledContainer
            label={`${intl.formatMessage({
              id: 'books.formsLabel.booksTime',
            })} (${intl.formatMessage({
              id: 'books.forms.pleaseSelectTime',
            })})`}
          >
            {timeSlotCandidates.map((timeSlot: TimeSlot) =>
              isSchoolTimeSlot(timeSlot) ? (
                <BooksSelectTimeInstructor
                  key={timeSlot.id}
                  value={timeSlot.id}
                  checked={
                    !!selectedTimeSlots.find((t) => t.id === timeSlot.id)
                  }
                  // @ts-expect-error strictFunctionTypes
                  onChange={handleClickTimeSlot}
                  startTime={timeSlot.startTime}
                  endTime={timeSlot.endTime}
                  vacancy={timeSlot.vacancy}
                  showVacancy={showVacancy}
                  instructors={timeSlot.instructors.map((i) => i.name)}
                />
              ) : (
                <BooksSelectTimeEvent
                  key={timeSlot.id}
                  value={timeSlot.id}
                  postable={postable}
                  checked={
                    !!selectedTimeSlots.find((t) => t.id === timeSlot.id)
                  }
                  // @ts-expect-error strictFunctionTypes
                  onChange={handleClickTimeSlot}
                  startTime={timeSlot.startTime}
                  endTime={timeSlot.endTime}
                  vacancy={timeSlot.vacancy}
                  showVacancy={showVacancy}
                  attendees={attendees || 1}
                  waitingListProvided={!!waitingList}
                  setNotification={
                    waitingList
                      ? !!waitingList.list.find((i) => i === timeSlot.id)
                      : false
                  }
                  // @ts-expect-error strictFunctionTypes
                  onClickSetNotification={handleAddToWaitingList}
                />
              ),
            )}
          </LabeledContainer>
        ) : (
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.booksTime',
            })}
            contentsPadding={true}
          >
            <span
              className={css.InputSchoolCalendarDateTimePicker__PlaceHolder}
            >
              {intl.formatMessage({
                id: 'books.forms.pleaseSelectDate',
              })}
            </span>
          </LabeledContainer>
        )}
        <div className="px-[18px] py-5">
          <RSButton
            variant="basic"
            isBlock
            disabled={!selectedDate || selectedTimeSlots.length === 0}
            onClick={handleClickCommit}
          >
            {intl.formatMessage({ id: 'books.submitLabel.addThisDateAndTime' })}
          </RSButton>
        </div>
      </div>
    </BooksLoadingMask>
  );
};

export default InputSchoolCalendarDateTimePicker;
